import { Eye, EyeOff, Shuffle, X } from 'lucide-react'
import { useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { GROUP_TYPES, isStarter, pickStarter } from '../lib/groupTypes'
import { cn } from '../lib/utils'
import { createGroup } from '../services/groupService'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Toggle } from './ui/toggle'

export default function CreateGroupDialog({ onClose, onCreated }) {
  const notify = useNotification()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [groupType, setGroupType] = useState(null)
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) {
      notify.error('Please enter a group name')
      return
    }

    setCreating(true)
    try {
      const result = await createGroup({
        name: name.trim(),
        description: description.trim() || null,
        isPublic: true,
        isPrivate,
        groupType
      })
      notify.success(`Group "${name.trim()}" created successfully!`)
      onCreated?.(result?.group_id)
    } catch (err) {
      console.error('Error creating group:', err)
      notify.error(err.message || 'Failed to create group')
      setCreating(false)
    }
  }

  // Picking a type fills in a starter description, unless the owner has
  // already written their own.
  const chooseType = (key) => {
    setGroupType(key)
    if (!description.trim() || isStarter(description)) setDescription(pickStarter(key, description))
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Create Group</CardTitle>
              <CardDescription>Start your own group</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block" htmlFor="new-group-name">Group Name *</label>
              <Input
                id="new-group-name"
                placeholder="My Awesome Group"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <fieldset>
              <legend className="text-sm font-medium mb-2">What kind of group is this?</legend>
              <div className="space-y-2">
                {GROUP_TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => chooseType(t.key)}
                    aria-pressed={groupType === t.key}
                    className={cn(
                      'w-full text-left rounded-lg border p-3 transition-colors',
                      groupType === t.key ? 'border-primary bg-primary/5' : 'hover:bg-muted/60'
                    )}
                  >
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.hint}</p>
                  </button>
                ))}
              </div>
            </fieldset>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" htmlFor="new-group-description">Description</label>
                {groupType && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setDescription(pickStarter(groupType, description))}
                  >
                    <Shuffle className="h-3.5 w-3.5 mr-1" />
                    Try another
                  </Button>
                )}
              </div>
              <textarea
                id="new-group-description"
                placeholder="A group for pickleball enthusiasts..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            {/* Visibility: the label names the current state, the subtext names what the toggle does */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                {isPrivate ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-primary" />
                )}
                <div>
                  <p className="text-sm">
                    <span className="font-semibold">{isPrivate ? 'Private:' : 'Public:'}</span>{' '}
                    {isPrivate ? 'Invitation link required' : 'Anyone can join'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isPrivate ? 'Toggle to make it public' : 'Toggle to make it private'}
                  </p>
                </div>
              </div>
              <Toggle checked={isPrivate} onCheckedChange={setIsPrivate} />
            </div>

            <p className="text-xs text-muted-foreground">
              Every bracket you enter is added to your group automatically. You can remove any of them.
            </p>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="primaryNeon"
                onClick={handleCreate}
                className="flex-1"
                disabled={!name.trim() || creating}
              >
                {creating ? 'Creating...' : 'Create Group'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
